import { Link } from 'react-router-dom';
import { Sparkles, Radar, Video, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { AdvertiseHero } from '@/components/advertise/AdvertiseHero';
import { AdvertiseBenefitsGrid } from '@/components/advertise/AdvertiseBenefitsGrid';
import { AdvertiseFAQ } from '@/components/advertise/AdvertiseFAQ';
import { ADVERTISE_SELLERS } from '@/constants/testIds/advertise';

const BENEFITS = [
  { icon: Sparkles, title: 'Premium placement', desc: 'Your home is boosted to the top of search results and map view in your area.' },
  { icon: Radar, title: 'Buyer retargeting', desc: 'Bring shoppers back with retargeting ads after they view your listing.' },
  { icon: Video, title: '3D tour support', desc: 'Add a Matterport walkthrough so buyers can explore your home any time.' },
  { icon: Clock, title: 'Active until sold', desc: 'One flat fee — your boost stays active the entire time your home is on the market.' },
];

const FAQS = [
  ['Do I need to be a real estate agent?', 'No. Listing Boost is built for homeowners selling on their own, as well as listing agents who want extra push on one property.'],
  ['How much does it cost?', 'Pricing is based on your home\u2019s list price, shown before you pay — most homes qualify for our base rate.'],
  ['How long does the boost last?', 'Your boost stays active for as long as your listing is on the market, at no extra charge.'],
  ['What if I don\u2019t see my listing during setup?', 'You can still continue — just enter your home\u2019s details manually and we\u2019ll match it once it\u2019s live.'],
];

export default function SellersLanding() {
  const revealRef = useScrollReveal();
  return (
    <div ref={revealRef}>
      <AdvertiseHero
        eyebrow="For home sellers"
        title="Boost your listing straight to the top"
        subtitle="A one-time Listing Boost gets your home premium placement, retargeting ads, and a 3D tour — no agent or membership required."
        primaryCta={{ label: 'Boost my listing', to: '/advertise/sellers/address', testId: ADVERTISE_SELLERS.landingCta }}
        stat="Flat one-time fee · Active until your home is sold"
      />

      <section className="bg-sky-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center">
            <h2 className="font-display text-2xl font-semibold text-slate-900 sm:text-3xl">What you get with Listing Boost</h2>
          </div>
          <div className="mt-8"><AdvertiseBenefitsGrid items={BENEFITS} columns={4} /></div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl font-semibold text-slate-900 sm:text-3xl">Ready in under 5 minutes</h2>
        <p className="mt-2 text-slate-600">Enter your address, confirm your details, and check out — your boost goes live right away.</p>
        <Button asChild size="lg" className="mt-6 rounded-full"><Link to="/advertise/sellers/address">Get started</Link></Button>
      </section>

      <AdvertiseFAQ items={FAQS} />
    </div>
  );
}
